import { normalizePackageSelection } from '@/lib/package-utils'
import { useEffect, useMemo, useState } from 'react'
import { DropdownCountry1 } from '@/components/DropdownCountry'
import useSWR from 'swr'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
} from '@tanstack/react-table'
import DropdownPackageName, {
  usePackageName,
} from '@/components/DropdownPackage'
import { usePid2 } from '@/components/DropdownPid'
import DataTableWithBorder from '@/components/zthd/data-table/data-table-with-border'
import { getColumns } from '@/components/roas/camps/data-table-columns'
import { useUrlParamsStore } from '@/stores/url-params-store'
import SourceSelect from '@/components/roas/source-select'
import AppSelect from '@/components/zthd/app-select'
import StatusSelect from '@/components/roas/camps/status-select'
import UserSelect from '@/components/monday/user-select'
import { useAuthStore } from '@/hooks'
import Loader from '@/components/zthd/Loader'
import { Button } from '@/components/ui/button'
import { RefreshCcw } from 'lucide-react'
import { MyDatePicker, useDatePicker2 } from '@/components/MyDatePicker'

const CampsPage = () => {
  const params = useUrlParamsStore((state) => state.params)
  const setParam = useUrlParamsStore((state) => state.setParam)
  const setParams = useUrlParamsStore((state) => state.setParams)
  const resetParams = useUrlParamsStore((state) => state.resetParams)
  const id = useAuthStore((state) => state.auth?.user?.id)

  const [sorting, setSorting] = useState([])
  const updatePackageName = usePackageName((state) => state.update)

  const { startDate, endDate } = useDatePicker2()

  // 更新 URL 参数的函数
  const updateSearchParams = (key, value) => {
    if (key === 'pid') {
      updatePackageName(normalizePackageSelection(value, usePackageName.getState().value))
    }
    setParam(key, value)
  }

  // 初始化参数：只在组件挂载时执行一次
  useEffect(() => {
    // 只在 URL 参数为空时初始化默认值
    const currentParams = useUrlParamsStore.getState().params
    const hasParams = Object.keys(currentParams).length > 0
    if (!hasParams) {
      resetParams()
      setParams({
        pid: '1',
        state: '0',
        country: 'total',
        mediaSource: 'all',
        owner: id ?? undefined,
        startDate: startDate,
        endDate: endDate,
      })
      updatePackageName('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // 只在挂载时执行一次

  // 同步日期变化：只更新日期参数，不重置其他过滤条件
  useEffect(() => {
    // 只在日期变化时更新日期参数，保留其他过滤条件
    setParam('startDate', startDate)
    setParam('endDate', endDate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate])

  useEffect(() => {
    usePid2.getState().updateSingle(params.pid)
  }, [params.pid])

  useEffect(() => {
    const unsubscribe = usePackageName.subscribe((state) => {
      updateSearchParams('packageName', state.value)
    })
    return unsubscribe
  }, [updateSearchParams])

  useEffect(() => {
    // on component unmount
    return () => {
      updatePackageName('')
    }
  }, [updatePackageName])

  const { data, isLoading, mutate: mutateCampaigns } = useSWR(['/campaigns', params])

  const days = [0, 3, 7, 14]
  const { data: roasData, mutate: mutateRoas } = useSWR([
    '/campaigns/roas',
    { ...params, days: days.join(',') },
  ])

  const openAllCampaigns = () => {
    camps.forEach((camp) => {
      if (camp.id.includes('_')) {
        return
      }
      window.open(`/troas/campaigns/${camp.id}`, '_blank')
    })
  }

  const camps = useMemo(() => {
    if (!data) return []
    return data.map((camp) => ({
      ...camp,
      daysRoas: roasData?.[camp.id] || {},
    }))
  }, [data, roasData])

  const columns = useMemo(() => getColumns(days), [days])

  const table = useReactTable({
    data: camps,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  })

  const handleRefresh = () => {
    mutateCampaigns()
    mutateRoas()
  }

  return (
    <div className="flex flex-col gap-4 py-4 sm:gap-2 sm:py-2">
      <div className="flex items-center gap-4 flex-wrap sm:gap-2 sm:py-2 xs:py-1 xs:gap-1">
        <AppSelect
          value={params.pid || 'total'}
          onValueChange={(value) => updateSearchParams('pid', value)}
        />
        <DropdownPackageName />
        <DropdownCountry1
          value={params.country || 'total'}
          update={(value) => updateSearchParams('country', value)}
        />
        <SourceSelect
          value={params.mediaSource || 'all'}
          onValueChange={(value) => updateSearchParams('mediaSource', value)}
        />
        <StatusSelect
          value={params.state || ''}
          onValueChange={(value) => updateSearchParams('state', value)}
        />
        <UserSelect
          value={params.owner || ''}
          onValueChange={(value) => updateSearchParams('owner', value)}
        />

        <MyDatePicker
          datePickerStore={useDatePicker2}
          getMaxDate="roas-complete"
          showMaxDate={true}
          defaultAllowIncomplete={false}
        />
        <div className="flex items-center gap-2 ml-auto">
          <Button variant="outline" onClick={openAllCampaigns}>
            Open All
          </Button>
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCcw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>
      {isLoading && <Loader>Loading...</Loader>}
      <DataTableWithBorder className="w-full" table={table} />
    </div>
  )
}

export default CampsPage
